import { PinDrop as MapIcon } from '@mui/icons-material';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { Show } from '@side/pantry';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { useSideListings } from '../../hooks';
import { SectionContainer } from '../SectionContainer';
import { ListingCard } from './components/ListingCard';
import { ListingCardGridSkeleton } from './components/ListingCardGridSkeleton';
import { ListingStatusTotals } from './components/ListingStatusTotals';
import { StyledNextLink } from './components/StyledNextLink';

export function SideListings() {
  const trackEvent = useTrackHomePageAnalytics();
  const {
    featuredListings,
    totalListings,
    offMarketListings,
    newOffMarketListings,
    comingSoonListings,
    newComingSoonListings,
    loading,
  } = useSideListings();

  if (!featuredListings.length && !loading) return null;

  return (
    <SectionContainer
      data-testid="txm-home-side-listings-section"
      sx={{ pt: 4, pb: 4 }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ gap: { xs: 4, md: 6, lg: 10 }, justifyContent: 'space-between' }}
      >
        <Stack spacing={2} sx={{ minWidth: 'max-content' }}>
          <Typography variant="h2" sx={{ color: 'other.spaceCadet' }}>
            Listing Feed
          </Typography>
          <ListingStatusTotals
            loading={loading}
            label="Off Market Listings"
            total={offMarketListings}
            totalNew={newOffMarketListings}
          />
          <ListingStatusTotals
            loading={loading}
            label="Coming Soon Listings"
            total={comingSoonListings}
            totalNew={newComingSoonListings}
          />

          <Button
            id="explore-side-listings-button"
            variant="contained"
            href="/community/listings?utm_source=hp&utm_medium=ltc"
            LinkComponent={StyledNextLink}
            onClick={() => {
              trackEvent(TrackHomePageEvent.SideListingsButtonClick);
            }}
          >
            Explore Listing Feed
          </Button>
        </Stack>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Typography
            variant="body3"
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              pt: 0.25,
            }}
          >
            <Show
              when={!loading}
              fallback={<Skeleton variant="text" sx={{ width: '220px' }} />}
            >
              <MapIcon sx={{ height: 14 }} />
              {`Showing ${featuredListings.length} of ${totalListings.toLocaleString()} listings`}
            </Show>
          </Typography>
          <Show when={!loading} fallback={<ListingCardGridSkeleton />}>
            <Box
              data-testid="SideListings__Featured"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                columnGap: { xs: 1.5, md: 2.5 },
                rowGap: { xs: 1.5, md: 2 },
              }}
            >
              {featuredListings.map((listing, index) => (
                <ListingCard key={listing.id} listing={listing} index={index} />
              ))}
            </Box>
          </Show>
        </Stack>
      </Stack>
    </SectionContainer>
  );
}
